import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
	useRef,
} from 'react';
import { preloadList, text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingCalculationData,
	BearingInfo,
	BearingSpringRatesPayload,
	BearingTypeCode,
	TimkenSpringRatesPreload,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import { BearingGroupOption, SelectOption, XAxisLimit, YAxisLimit } from '../types/types';
import {
	getBearingClasses,
	getBearingSpringRates,
	bearingGroups,
	additionalText,
} from '../../components/bearing-search/api/api';
import springRatesReducer, { bearingSpringRateChartReducer, initialBearingSpringRateChartState, initialSpringRatesState } from './reducer';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import {
	getDimensionLabel,
	toggleImportantNote,
	toggleNextTools,
	getTooltip,
	getPartNumberFromUrl,
	updateToolParams,
	getPartStateFromParams,
	toggleReportReady,
} from '../utils/engineering-tools';
import BearingTypeDropdown from '../components/bearing-type-dropdown/BearingTypeDropdown';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import {
	trackStart,
	trackResults,
	updateActionParams,
} from '../utils/analytics';
import { Chart, registerables } from 'chart.js';
import BearingSpringRatesChartForm from './BearingSpringRatesChartForm';
import PartPreSearch from '../components/PartPreSearch';
import ResultsTable from '../components/ResultsTable';

const BearingSpringRates: React.FC = () => {
	const calculationsRef = useRef<HTMLDivElement>(null);
	const [ preselectedTypes, setPreselectedTypes ] = useState<string[]>( [] );
	const [ bearingTypesGroups, setBearingTypesGroups ] = useState<BearingGroupOption[]>( [] );
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isBearingClassesLoading, setIsBearingClassesLoading ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ isComplete, setIsComplete ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ radialData, setRadialData ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ axialData, setAxialData ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ xAxisLimits, setXAxisLimits ] = useState<XAxisLimit>( null );
	const [ yAxisLimits, setYAxisLimits ] = useState<YAxisLimit>( null );
	const [ dimensionLabel, setDimensionLabel ] = useState( '' );
	const [ bearingClasses, setBearingClasses ] = useState<SelectOption<string>[]>(
		[],
	);
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>( null );
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		springRatesReducer,
		initialSpringRatesState,
	);
	const [ chartState, chartDispatch ] = useReducer(
		bearingSpringRateChartReducer,
		initialBearingSpringRateChartState,
	);

	const selectTypes = ( typeData: { partType: string[]; bearingType: string[]; bearingSubType: string[]; } ) => {
		const selectedPartType = typeData.partType;
		dispatch({
			type: 'change_part_type',
			payload: selectedPartType,
		});
	};

	const selectPreload = ( event: ChangeEvent<HTMLSelectElement> ) => {
		const value = event.target.value as unknown as TimkenSpringRatesPreload;
		dispatch( {
			type: 'change_spring_rates_preload',
			payload: value,
		} );
		setIsCalculated(false);
		toggleNextTools(false);
		toggleImportantNote(false);
	};

	Chart.register(...registerables);

	// tool analytics start up
	useEffect(() => {
		trackStart( 'bearingSpringRates' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, []);

	useEffect( () => {
		const isShown = isCalculated && ! isResultsLoading;

		toggleNextTools( isShown );
		toggleReportReady(
			isShown,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => toggleNextTools( false );
	}, [ isCalculated, isResultsLoading ] );

	useEffect( () => {
		if ( partState.bearingType ) {
			setIsBearingClassesLoading( true );
			getBearingClasses( {
				bearingType: partState.bearingType,
				designUnit:
					partState.bearingType === BearingTypeCode.TRB
						? partState.designUnit
						: undefined,
			} )
				.then( ( response ) => {
					const options = response.bearingClassList.map( ( item ) => {
						return {
							id: item.value,
							value: item.value,
							label: item.displayLabel,
						} as SelectOption<string>;
					} );
					if ( options.length > 0 ) {
						setBearingClasses( options );
						dispatch( {
							type: 'change_bearing_class',
							payload: options[ 0 ]?.value,
						} );
					}
				} )
				.finally( () => {
					setIsBearingClassesLoading( false );
				} );
		}
	}, [ partState.bearingType ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}
		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsCalculated( false );
		setIsPartSelected( false );
		toggleImportantNote( false );
		// Reset bearing type dropdown
		setPreselectedTypes( [] );

		partDispatch( {
			type: 'reset',
		} );
		dispatch( {
			type: 'reset',
		} );
	};

	useEffect(() => {
		if (isResultsLoading) {
			calculationsRef?.current?.scrollIntoView( {
				behavior: 'smooth',
			});
		}
	}, [ isResultsLoading ]);

	useEffect(() => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [partState.marketPlaceDescription]);

	const handleSubmit = ( event: FormEvent ) => {
		event.preventDefault();
		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		} else {
			// Calculate Spring Rates

			// Validated
			dispatch( {
				type: 'change_validation_flag',
				payload: true,
			} );

			setIsComplete( false );
			setIsCalculated( true );
			setIsResultsLoading( true );
			toggleImportantNote( false );

			const { partNumber, cupPartNumber, bearingType, bearingSubType } =
				partState;

			const request: Partial<BearingSpringRatesPayload> = {
				partNumber,
				cupPartNumber,
				bearingType,
				bearingSubType,
				...formState,
			};
			// match up the field name with the payload property expected
			request.preload = parseInt(formState.springRatesPreload) === 1;

			getBearingSpringRates( request )
				.then( ( response ) => {
					updateActionParams('bearingSpringRates', partState );
					let axialReportData: Array<BearingCalculationData | null> = [];
					let radialReportData: Array<BearingCalculationData | null> = [];
					if ( response.report && response.report.length !== 0 ) {
						axialReportData = response.report[ 0 ].axial.map((item) => ({
							...item,
						}));
						radialReportData = response.report[ 0 ].radial.map((item) => ({
							...item,
						}));
						// sorting data by radial and axial value to ensure they are consistently ordered correctly
						radialReportData.forEach((data) => {
							if (data?.sectionData) {
								data.sectionData.sort((a, b) => {
									const radialValueA = parseInt(a[ 0 ]); //convert from str to int
									const radialValueB = parseInt(b[ 0 ]);
									return radialValueA - radialValueB;
								});
							}
						});
						axialReportData.forEach((data) => {
							if (data?.sectionData) {
								data.sectionData.sort((a, b) => {
									const axialValueA = parseInt(a[ 0 ]);
									const axialValueB = parseInt(b[ 0 ]);
									return axialValueA - axialValueB;
								});
							}
						});
						setRadialData( radialReportData );
						setAxialData( axialReportData );
						prepareXLimits( radialReportData, axialReportData );
						prepareYLimits( radialReportData, axialReportData );
					}

					const resultsLength = radialReportData.length && axialReportData.length ? 1 : 0;
					trackResults( 'bearingSpringRates', getAnalyticsPayload(request), resultsLength, partState.marketPlaceDescription );
					setDimensionLabel( getDimensionLabel( partState, formState.unitSystem ) );
					toggleImportantNote( true );
				} )
				.finally( () => {
					setIsResultsLoading( false );
					setIsComplete( true );
				});
		}
	};

	const getAnalyticsPayload = (payload: Partial<BearingSpringRatesPayload>) => {
		const { partNumber, unitSystem } = payload;
		const { selectedPartTypes, springRatesPreload } = formState;

		return {
			partNumber,
			unitSystem,
			...(selectedPartTypes && selectedPartTypes.length && { selectedPartTypes }),
			springRatesPreload,
		};
	};

	const prepareXLimits = ( radialReportData, axialReportData ) => {
		const xLimits = {
			radial: {
				min: Math.min(...radialReportData[0].sectionData.map( row => row[1].replace(/,/g, '') )),
				max: Math.max(...radialReportData[0].sectionData.map( row => row[1].replace(/,/g, '') ))
			},
			axial: {
				min: Math.min(...axialReportData[0].sectionData.map( row => row[1].replace(/,/g, '') )),
				max: Math.max(...axialReportData[0].sectionData.map( row => row[1].replace(/,/g, '') ))
			}
		};

		setXAxisLimits(xLimits);
	};

	const prepareYLimits = ( radialReportData, axialReportData ) => {
		const yLimits = {
			radial: {
				deflection: {
					min: Math.min(...radialReportData[0].sectionData.map( row => row[2].replace(/,/g, '') )),
					max: Math.max(...radialReportData[0].sectionData.map( row => row[2].replace(/,/g, '') ))
				},
				springRate: {
					min: Math.min(...radialReportData[0].sectionData.map( row => row[3].replace(/,/g, '') )),
					max: Math.max(...radialReportData[0].sectionData.map( row => row[3].replace(/,/g, '') ))
				}
			},
			axial: {
				deflection: {
					min: Math.min(...axialReportData[0].sectionData.map( row => row[2].replace(/,/g, '') )),
					max: Math.max(...axialReportData[0].sectionData.map( row => row[2].replace(/,/g, '') ))
				},
				springRate: {
					min: Math.min(...axialReportData[0].sectionData.map( row => row[3].replace(/,/g, '') )),
					max: Math.max(...axialReportData[0].sectionData.map( row => row[3].replace(/,/g, '') ))
				}
			}
		};

		setYAxisLimits(yLimits);
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};
	const setVerifiedPartNumberAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput(value);
		handleSubmit(e);
	};

	const handleFormChange = () => {
		dispatch( {
			type: 'change_validation_flag',
			payload: false,
		} );
	};

	const getTableHeader = (dataType: 'radial' | 'axial') => {
		//@todo: Use the data from the response to generate the header labels
		const linearUnit = formState.unitSystem === 'M' ? 'mm' : 'in';
		const forceUnit = formState.unitSystem === 'M' ? 'N' : 'lbf';
		const radialHeader = formState.springRatesPreload === '1' ? 'Radial<br />(360 LZ) (% C90)' : 'Radial<br />(180 LZ) (% C90)';
		const headerLabel = dataType === 'radial' ? radialHeader : 'Axial<br />(% Ca90)';
		return `${ headerLabel }, Load<br />(${ forceUnit }), Deflection<br />(${ linearUnit }), Spring Rate<br />(${ forceUnit }/${ linearUnit })`.split(',');
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit} onChange={handleFormChange}>
					<div className="t-engineering-tools__form-fields">
						<div className="spring_rates_dropdown">
							<BearingTypeDropdown
								id="bearing_spring_rates_type"
								label={text.bearingType}
								placeholder={text.selectBearingType}
								groups={bearingGroups}
								tooltip={getTooltip(text.bearingType)}
								preselected={preselectedTypes}
								onChange={selectTypes}
								onApply={(groups) => setBearingTypesGroups(groups)}
							/>
						</div>
						<div className="relative-dropdown timken-tool-presearch-container">
							<TextInput
								id="bearing_spring_rates_part_number"
								label={text.partNumber}
								value={searchInput}
								placeholder={text.partNumber}
								onChange={inputPartNumber}
								className="dropdown-toggle"
								data-bs-toggle="dropdown"
								disabled={!! partState.partNumber}
								verified={!! partState.partNumber}
								message={
									partState.partNumber
										? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
										: text.partWildCardHint
								}
							/>
							<PartPreSearch
								input={searchInput}
								partType={formState.partType}
								unit={formState.unitSystem}
								onClick={preSearchClickHandler}
								isHidden={isPartSelected || isCalculated || isPartOpen}
								verifyPartNumber={(value: string, e) => setVerifiedPartNumberAndSubmit(value, e)}
							/>
						</div>
						<SelectDropdown
							id="bearing_tolerance_unit"
							name="unitSystem"
							required={true}
							label={text.displayUnits}
							value={formState.unitSystem}
							options={unitList}
							onChange={(event: ChangeEvent<HTMLSelectElement>) =>
								dispatch({
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								})
							}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
							>
								{text.lookup}
							</button>
						)}
					</div>

					{!! partState.partNumber && (
						<>
							<div className="row">
								<div className="col-md-3">
									<SelectDropdown
										id="bearing_spring_rates_preload"
										name="springRatesPreload"
										required={false}
										label={text.preload}
										tooltip={getTooltip(text.preload)}
										value={formState.springRatesPreload}
										options={preloadList}
										onChange={selectPreload}
									/>
								</div>
							</div>
						</>
					)}

					{!! partState.partNumber && (
						<div>
							<div className="t-engineering-tools__terms-of-use"
								 dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
							/>
							<div className="t-engineering-tools__search-actions">
								<button
									className="btn btn--primary"
									type="submit"
									disabled={formState.isValidated}
								>
									{text.calculate}
								</button>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>
			<>
				{isCalculated && (
					<div className="t-engineering-tools__results" ref={calculationsRef}>
						<h2>{text.results}</h2>

						<p className="t-engineering-tools__results-link d-none">
							<a href="#">{text.disclaimer}</a>
						</p>
						<div className="calculation-results-container">
							<div className="t-engineering-tools__results__table">
								<ResultsTable
									loading={isResultsLoading}
									table={{
										sectionName: 'Spring Rates',
										sectionHeading: getTableHeader('radial'),
										sectionData: radialData[0]?.sectionData || [],
										sectionHeadingTable: text.radialResults,
										additional: additionalText
									}}
									label={dimensionLabel}
								/>
								<ResultsTable
									loading={isResultsLoading}
									table={{
										sectionName: '',
										sectionHeading: getTableHeader('axial'),
										sectionHeadingTable: text.axialResults,
										sectionData: axialData[0]?.sectionData || []
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</>

			{isCalculated && radialData.length > 0 && (
				<BearingSpringRatesChartForm
					data={chartState.graphType === 'radial' ? chartState.radialGraphData : chartState.axialGraphData}
					xAxisLabel={formState.springRatesPreload == 1 ? 'Radial Load (with preload)' : 'Radial Load'}
					yAxisLabel={'Radial Deflection'}
					title={chartState.graphTitle}
					xAxisMin={xAxisLimits.radial.min}
					xAxisMax={xAxisLimits.radial.max}
					yAxisMin={yAxisLimits.radial.deflection.min}
					yAxisMax={yAxisLimits.radial.deflection.max}
					yAxisScaling={chartState.yAxisScaling}
					yAxisType={chartState.yAxisType}
					loading={isResultsLoading}
					partState={partState}
					formState={formState}
					xAxisLimits={xAxisLimits}
					yAxisLimits={yAxisLimits}
					complete={isComplete}
				/>
			)}

			{isPartOpen && (
				<PartSearch
					units={formState.unitSystem}
					partNumber={searchInput}
					partType={formState.partType}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};
export default BearingSpringRates;
